import { useCallback, useContext, useEffect } from 'react';
import styled from '@emotion/styled';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { StopCircle } from 'react-feather';
import {
  InitiateQuerySpeechToText,
  TextToSpeechStateContext,
  StopTextToSpeechContext,
  VoiceModeContext,
} from '@/containers/TextToSpeechAudioPlayer/Context';
import { ChatResponseType } from '@/enums';
import { useHasBetaPermission, useHasChatReadAloudEnabled } from '@/store/selectors';
import { ActivityIndicator } from '../presentation';
import { BaseMessageButton } from './Message.Button';

type Props = {
  kolSearchId?: number | null;
  queryId?: number | null;
};

export const ReadAloudButton = ({ kolSearchId, queryId }: Props) => {
  const hasBetaPermission = useHasBetaPermission();
  const hasChatReadAloudEnabled = useHasChatReadAloudEnabled();

  const initiateReadAloud = useContext(InitiateQuerySpeechToText);
  const stopTextToSpeech = useContext(StopTextToSpeechContext);
  const audioState = useContext(TextToSpeechStateContext);
  const voicemode = useContext(VoiceModeContext);

  const isActive = audioState && (audioState.kolSearchId === kolSearchId || audioState.queryId === queryId);
  const isPlaying = audioState?.state === 'playing' && isActive;
  const isLoading = audioState?.state === 'loading' && isActive;
  const isError = audioState?.state === 'error' && isActive;

  const startPlaying = useCallback(async () => {
    if (isPlaying) {
      stopTextToSpeech?.();
      return;
    }

    return initiateReadAloud({
      kolSearchId,
      queryId,
    });
  }, [isPlaying, stopTextToSpeech, initiateReadAloud, kolSearchId, queryId]);

  const autoPlayReadAloud = useCallback(() => {
    if (voicemode.enabled && voicemode.type !== ChatResponseType.HcpSummary && voicemode.queryId === queryId) {
      if (audioState.state === 'playing') {
        stopTextToSpeech?.();
      }
      startPlaying();
      voicemode.dequeue();
    }
  }, [
    audioState,
    startPlaying,
    queryId,
    stopTextToSpeech,
    voicemode,
  ]);

  useEffect(() => {
    autoPlayReadAloud();
  }, []);

  if (!hasBetaPermission || !hasChatReadAloudEnabled) return null;

  if (isPlaying) {
    return (
      <StyledButton onClick={stopTextToSpeech}>
        <FilledStopCircle size={18} />
        Stop
      </StyledButton>
    );
  }

  if (isLoading) {
    return (
      <StyledButton>
        <ActivityIndicator />
      </StyledButton>
    );
  }

  if (isError) {
    return (
      <StyledButton>
        Error
      </StyledButton>
    );
  }

  return (
    <StyledButton onClick={startPlaying}>
      <PlayArrowIcon fontSize="small" />
      Read Aloud
    </StyledButton>
  );
};

const StyledButton = styled(BaseMessageButton)({
  display: 'flex',
  justifyContent: 'left',
  gap: '4px',
  width: 115,
});

const FilledStopCircle = styled(StopCircle)`
  circle {
    fill: none;
  }
  rect {
    fill: currentColor;
  }
`;
